/**
 * stock_trades.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Admin stock trades management — view users' stock orders.
 * Route: /admin/stock-trades
 *
 * Tables used:
 *   stock_orders  (id, user_id → users, symbol → stocks,
 *                  side, order_type, quantity, limit_price,
 *                  status, filled_price, created_at)
 *   stocks        (symbol, name, sector, exchange, color_hex, initials)
 *   users         (id, first_name, last_name, email)
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import supabase from "../supabase";
import {
  // sidebar
  LayoutDashboard, Wallet, Receipt, Copy, UserCheck,
  HeadphonesIcon, Bell, CandlestickChart, BarChart2, ShoppingBag,
  ChevronRight, LogOut, Users,CreditCard, MapPin,
  // page
  Search, RefreshCw, Eye, X, TrendingUp, TrendingDown,
  AlertCircle, CheckCircle2, Clock, ChevronUp, ChevronDown,
  Activity, Package, XCircle, Plus, Menu,
} from "lucide-react";

const PER_PAGE = 15;

// ─── Sidebar nav ──────────────────────────────────────────────────────────────
const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true }],
  },
  {
    label: "Users & Finance",
    items: [
      { to: "/admin/users",        label: "Users",        icon: Users   },
      { to: "/admin/wallets",      label: "Wallets",      icon: Wallet  },
      { to: "/admin/transactions", label: "Transactions", icon: Receipt },
    ],
  },
  {
    label: "Trading",
    items: [
      { to: "/admin/copy-trades",  label: "Copy trades",   icon: Copy             },
      { to: "/admin/traders",      label: "Hired traders", icon: UserCheck        },
      { to: "/admin/trading",      label: "Open trades",   icon: CandlestickChart },
      { to: "/admin/stock-trades", label: "Stock trades",  icon: BarChart2        },
      { to: "/admin/marketplace",  label: "Marketplace",   icon: ShoppingBag      },
    ],
  },
  {
    label: "Communication",
    items: [
      { to: "/admin/support",       label: "Support",       icon: HeadphonesIcon },
      { to: "/admin/notifications", label: "Notifications", icon: Bell           },
    ],
  },
   {
    label: "Settings",
    items: [
      { to: "/admin/subscriptions",      label: "Subscriptions",      icon: CreditCard },
      { to: "/admin/payment-addresses",  label: "Payment Addresses",  icon: MapPin     },
      { to: "/admin/market-subscriptions",  label: "Market Subscriptions",  icon: MapPin     },
    ],
  },
];

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg-base:      #06060f;
    --bg-surface:   #0a0a16;
    --bg-elevated:  #0e0e1c;
    --bg-card:      #111122;
    --bg-hover:     #14142a;
    --border:       rgba(255,255,255,0.055);
    --border-hi:    rgba(255,255,255,0.11);
    --border-glow:  rgba(99,102,241,0.3);
    --accent:       #6366f1;
    --accent-light: #818cf8;
    --accent-dim:   rgba(99,102,241,0.13);
    --accent-glow:  rgba(99,102,241,0.22);
    --green:        #34d399;
    --green-dim:    rgba(52,211,153,0.13);
    --amber:        #fbbf24;
    --amber-dim:    rgba(251,191,36,0.13);
    --rose:         #fb7185;
    --rose-dim:     rgba(251,113,133,0.13);
    --cyan:         #22d3ee;
    --cyan-dim:     rgba(34,211,238,0.13);
    --purple:       #a78bfa;
    --purple-dim:   rgba(167,139,250,0.13);
    --text-primary:   #eeeeff;
    --text-secondary: #7878a0;
    --text-muted:     #3e3e58;
    --font-sans: 'Inter', system-ui, sans-serif;
    --font-mono: 'JetBrains Mono', monospace;
    --sidebar-w: 232px;
    --topbar-h:  60px;
    --radius-sm: 8px;
    --radius-md: 14px;
    --radius-lg: 18px;
  }

  html, body, #root { height: 100%; background: var(--bg-base); font-family: var(--font-sans); color-scheme: dark; }

  /* Shell */
  .st-shell {
    display: flex; height: 100vh; height: 100dvh; overflow: hidden;
    background: var(--bg-base); position: relative;
  }
  .st-shell::before {
    content: ''; position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
    opacity: 0.018;
  }

  /* ── Sidebar ── */
  .ad-sidebar {
    width: var(--sidebar-w); flex-shrink: 0;
    display: flex; flex-direction: column; height: 100%; overflow: hidden;
    background: var(--bg-surface); border-right: 1px solid var(--border);
    position: relative; z-index: 50;
    transition: transform .3s cubic-bezier(.4,0,.2,1); will-change: transform;
  }
  .ad-sidebar::after {
    content: ''; position: absolute; top: 0; right: 0; bottom: 0; width: 1px;
    background: linear-gradient(to bottom, transparent 0%, var(--accent-glow) 35%, rgba(99,102,241,0.08) 70%, transparent 100%);
    pointer-events: none;
  }
  .ad-logo {
    display: flex; align-items: center; gap: 11px;
    padding: 0 18px; height: var(--topbar-h);
    border-bottom: 1px solid var(--border); flex-shrink: 0;
  }
  .ad-logo-icon {
    width: 32px; height: 32px; border-radius: 10px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    box-shadow: 0 0 0 1px rgba(99,102,241,0.4), 0 4px 16px rgba(99,102,241,0.35), 0 0 32px rgba(99,102,241,0.15);
  }
  .ad-logo-text strong {
    font-size: 13.5px; font-weight: 800; letter-spacing: -0.4px;
    background: linear-gradient(to right, #eeeeff, #a5b4fc);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .ad-logo-text span { font-size: 9.5px; color: var(--text-muted); display: block; margin-top: 3px; letter-spacing: 0.8px; text-transform: uppercase; font-weight: 500; }
  .ad-nav { flex: 1; min-height: 0; overflow-y: auto; padding: 18px 10px; display: flex; flex-direction: column; gap: 22px; -webkit-overflow-scrolling: touch; scroll-behavior: smooth; }
  .ad-nav::-webkit-scrollbar { width: 3px; }
  .ad-nav::-webkit-scrollbar-track { background: transparent; }
  .ad-nav::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.18); border-radius: 99px; }
  .ad-nav-section-label { font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-muted); padding: 0 10px; margin-bottom: 5px; }
  .ad-nav-items { display: flex; flex-direction: column; gap: 1px; }
  .ad-nav-link {
    display: flex; align-items: center; gap: 9px; padding: 8px 10px; border-radius: var(--radius-sm);
    font-size: 12.5px; font-weight: 500; color: var(--text-secondary);
    text-decoration: none; transition: all 0.18s ease; position: relative;
  }
  .ad-nav-link:hover { background: var(--bg-hover); color: var(--text-primary); }
  .ad-nav-link.active {
    background: linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(139,92,246,0.10) 100%);
    color: var(--accent-light); font-weight: 600;
    box-shadow: inset 0 0 0 1px rgba(99,102,241,0.15);
  }
  .ad-nav-link.active::before {
    content: ''; position: absolute; left: 0; top: 18%; bottom: 18%;
    width: 2.5px; border-radius: 99px;
    background: linear-gradient(to bottom, var(--accent), var(--purple));
    box-shadow: 0 0 10px var(--accent-glow), 0 0 20px rgba(99,102,241,0.2);
  }
  .ad-nav-link svg { flex-shrink: 0; opacity: 0.65; transition: opacity 0.15s; }
  .ad-nav-link:hover svg, .ad-nav-link.active svg { opacity: 1; }
  .ad-nav-chevron { margin-left: auto; opacity: 0.35; }
  .ad-user-card { flex-shrink: 0; border-top: 1px solid var(--border); padding: 12px 10px; background: linear-gradient(to top, rgba(99,102,241,0.04) 0%, transparent 100%); }
  .ad-user-inner { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border-radius: var(--radius-sm); transition: background 0.15s; }
  .ad-user-inner:hover { background: var(--bg-hover); }
  .ad-user-avatar {
    width: 32px; height: 32px; border-radius: 50%;
    background: linear-gradient(135deg, var(--accent-dim), rgba(139,92,246,0.2));
    border: 1.5px solid rgba(99,102,241,0.35);
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; color: var(--accent-light); flex-shrink: 0;
  }
  .ad-user-info { flex: 1; min-width: 0; }
  .ad-user-info strong { font-size: 12px; font-weight: 600; color: var(--text-primary); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .ad-user-info span   { font-size: 10px; color: var(--text-muted); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
  .ad-logout-btn {
    width: 28px; height: 28px; border-radius: 7px; background: none; border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center; color: var(--text-muted); transition: all 0.15s;
  }
  .ad-logout-btn:hover { background: var(--rose-dim); color: var(--rose); }


  /* Topbar */
  .st-topbar {
    flex-shrink: 0; height: var(--topbar-h);
    background: rgba(10,10,22,0.85); backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 28px; position: relative;
  }
  .st-topbar::after {
    content: ''; position: absolute; bottom: -1px; left: 0; right: 0; height: 1px;
    background: linear-gradient(to right, transparent 0%, var(--accent-glow) 50%, transparent 100%);
  }
  .st-topbar-left  { display: flex; align-items: center; gap: 14px; }
  .st-topbar-right { display: flex; align-items: center; gap: 8px; }
  .st-topbar-badge {
    display: flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 99px;
    background: var(--accent-dim); border: 1px solid rgba(99,102,241,0.2);
    font-size: 10px; font-weight: 700; color: var(--accent-light); letter-spacing: 0.5px; text-transform: uppercase;
  }
  .st-topbar-title { font-size: 15px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.4px; }
  .st-topbar-sub   { font-size: 11px; color: var(--text-muted); margin-top: 1px; }

  /* Buttons */
  .st-btn {
    display: flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: var(--radius-sm);
    font-size: 11.5px; font-weight: 500; cursor: pointer; transition: all 0.18s; font-family: var(--font-sans);
    border: 1px solid var(--border-hi); background: var(--bg-card); color: var(--text-secondary);
  }
  .st-btn:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }
  .st-btn-accent {
    background: linear-gradient(135deg, rgba(99,102,241,0.22) 0%, rgba(139,92,246,0.15) 100%);
    color: var(--accent-light); border-color: rgba(99,102,241,0.3);
  }
  .st-btn-accent:hover { background: linear-gradient(135deg, rgba(99,102,241,0.32) 0%, rgba(139,92,246,0.22) 100%); }

  /* Main */
  .st-main { flex: 1; overflow-y: auto; padding: 26px 28px; display: flex; flex-direction: column; gap: 20px; }
  .st-main::-webkit-scrollbar { width: 4px; }
  .st-main::-webkit-scrollbar-track { background: transparent; }
  .st-main::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.25); border-radius: 99px; }

  /* Stat grid */
  .st-stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
  .st-stat {
    background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); padding: 18px 20px;
    display: flex; flex-direction: column; gap: 12px; position: relative; overflow: hidden;
    transition: border-color 0.22s, box-shadow 0.22s, transform 0.18s;
  }
  .st-stat::before {
    content: ''; position: absolute; inset: 0;
    background: radial-gradient(ellipse at 110% -10%, var(--stat-glow, transparent) 0%, transparent 60%);
    pointer-events: none;
  }
  .st-stat::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(to right, transparent 20%, var(--stat-bar, transparent) 50%, transparent 80%);
    pointer-events: none;
  }
  .st-stat:hover { border-color: rgba(255,255,255,0.1); box-shadow: 0 4px 24px rgba(0,0,0,0.4); transform: translateY(-1px); }
  .st-stat-top   { display: flex; align-items: flex-start; justify-content: space-between; }
  .st-stat-label { font-size: 9.5px; font-weight: 700; letter-spacing: 1.1px; text-transform: uppercase; color: var(--text-muted); }
  .st-stat-icon  { width: 32px; height: 32px; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .st-stat-value { font-size: 26px; font-weight: 700; color: var(--text-primary); letter-spacing: -1.5px; font-family: var(--font-mono); line-height: 1; }
  .st-stat-sub   { font-size: 10.5px; color: var(--text-muted); margin-top: 4px; }

  /* Tabs */
  .st-tabs { display: flex; gap: 2px; padding: 4px; background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); width: fit-content; }
  .st-tab {
    display: flex; align-items: center; gap: 7px; padding: 7px 16px; border-radius: 10px;
    font-size: 12.5px; font-weight: 500; cursor: pointer; border: none; background: none; transition: all 0.18s; font-family: var(--font-sans);
    color: var(--text-muted);
  }
  .st-tab:hover { color: var(--text-secondary); }
  .st-tab.active { background: linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(139,92,246,0.10) 100%); color: var(--accent-light); box-shadow: inset 0 0 0 1px rgba(99,102,241,0.15); }
  .st-tab-badge {
    min-width: 18px; height: 18px; padding: 0 5px; border-radius: 99px;
    font-size: 9.5px; font-weight: 700; display: flex; align-items: center; justify-content: center;
    background: rgba(255,255,255,0.07); color: var(--text-muted);
  }

  /* Status filter pills */
  .st-filter-wrap { display: flex; gap: 2px; padding: 3px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-sm); }
  .st-filter-pill {
    padding: 4px 11px; border-radius: 6px; font-size: 11px; font-weight: 500; cursor: pointer;
    border: none; background: none; font-family: var(--font-sans); color: var(--text-muted); transition: all 0.15s; text-transform: capitalize;
  }
  .st-filter-pill:hover { color: var(--text-secondary); }
  .st-filter-pill.active { background: var(--accent-dim); color: var(--accent-light); }

  /* Table card */
  .st-table-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); overflow: hidden; }
  .st-table-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; border-bottom: 1px solid var(--border); gap: 12px; flex-wrap: wrap; }
  .st-table-title { font-size: 13px; font-weight: 600; color: var(--text-primary); }
  .st-table-count { font-size: 10px; color: var(--text-muted); background: rgba(255,255,255,0.05); border: 1px solid var(--border); border-radius: 99px; padding: 2px 8px; }
  .st-search-wrap { position: relative; }
  .st-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-muted); pointer-events: none; }
  .st-search {
    padding: 7px 12px 7px 30px; background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm); font-size: 12px; color: var(--text-primary);
    font-family: var(--font-sans); outline: none; width: 200px; transition: border-color 0.18s;
  }
  .st-search::placeholder { color: var(--text-muted); }
  .st-search:focus { border-color: rgba(99,102,241,0.45); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }

  /* Table */
  .st-table { width: 100%; border-collapse: collapse; }
  .st-th {
    padding: 10px 16px; text-align: left; font-size: 9.5px; font-weight: 700;
    letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted);
    border-bottom: 1px solid var(--border); cursor: pointer; white-space: nowrap; user-select: none;
    transition: color 0.15s;
  }
  .st-th:hover { color: var(--text-secondary); }
  .st-th.no-sort { cursor: default; }
  .st-th.right { text-align: right; }
  .st-td { padding: 13px 16px; border-bottom: 1px solid rgba(255,255,255,0.03); vertical-align: middle; }
  .st-tr { transition: background 0.15s; }
  .st-tr:hover { background: rgba(255,255,255,0.018); }
  .st-tr:last-child .st-td { border-bottom: none; }

  /* Avatar */
  .st-avatar {
    width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; font-weight: 700;
  }
  .st-avatar-indigo { background: rgba(99,102,241,0.18); color: #818cf8; }
  .st-avatar-green  { background: rgba(52,211,153,0.18); color: #34d399; }
  .st-avatar-amber  { background: rgba(251,191,36,0.18);  color: #fbbf24; }
  .st-avatar-rose   { background: rgba(251,113,133,0.18); color: #fb7185; }
  .st-avatar-purple { background: rgba(167,139,250,0.18); color: #a78bfa; }

  /* Stock badge */
  .st-stock-badge {
    width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 8px; font-weight: 800; letter-spacing: 0.3px;
  }

  /* Chips */
  .st-chip {
    display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px;
    border-radius: 99px; font-size: 10px; font-weight: 600;
  }
  .st-chip-buy      { background: var(--green-dim);   color: var(--green); border: 1px solid rgba(52,211,153,0.25); }
  .st-chip-sell     { background: var(--rose-dim);    color: var(--rose);  border: 1px solid rgba(251,113,133,0.25); }
  .st-chip-open     { background: var(--amber-dim);   color: var(--amber); border: 1px solid rgba(251,191,36,0.25); }
  .st-chip-filled   { background: var(--green-dim);   color: var(--green); border: 1px solid rgba(52,211,153,0.25); }
  .st-chip-cancelled{ background: rgba(255,255,255,0.05); color: var(--text-muted); border: 1px solid var(--border); }
  .st-chip-market   { background: var(--accent-dim);  color: var(--accent-light); border: 1px solid rgba(99,102,241,0.2); }
  .st-chip-limit    { background: var(--cyan-dim);    color: var(--cyan);  border: 1px solid rgba(34,211,238,0.2); }

  /* Action btn */
  .st-icon-btn {
    width: 28px; height: 28px; border-radius: 7px; border: 1px solid var(--border);
    background: none; cursor: pointer; display: flex; align-items: center; justify-content: center;
    color: var(--text-muted); transition: all 0.15s;
  }
  .st-icon-btn:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }

  /* Pagination */
  .st-pagination { display: flex; align-items: center; justify-content: space-between; padding: 12px 20px; border-top: 1px solid var(--border); }
  .st-page-info  { font-size: 11px; color: var(--text-muted); }
  .st-page-btns  { display: flex; gap: 3px; }
  .st-page-btn   {
    width: 28px; height: 28px; border-radius: 6px; border: 1px solid var(--border);
    background: none; cursor: pointer; font-size: 11px; color: var(--text-muted); font-family: var(--font-sans);
    display: flex; align-items: center; justify-content: center; transition: all 0.15s;
  }
  .st-page-btn:hover { background: var(--bg-hover); color: var(--text-primary); }
  .st-page-btn.active { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }

  /* Empty state */
  .st-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 52px 20px; gap: 10px; color: var(--text-muted); }
  .st-empty-icon { width: 40px; height: 40px; border-radius: 12px; background: rgba(255,255,255,0.04); border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; }

  /* Skeleton */
  .st-skel { background: rgba(255,255,255,0.05); border-radius: 4px; animation: st-pulse 1.4s ease-in-out infinite; }
  @keyframes st-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

  /* Modal */
  .st-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.65); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 20px; }
  .st-modal {
    background: var(--bg-elevated); border: 1px solid var(--border-hi);
    border-radius: var(--radius-lg); width: 100%; max-width: 500px;
    box-shadow: 0 24px 80px rgba(0,0,0,0.6); overflow: hidden;
  }
  .st-modal-head { display: flex; align-items: flex-start; justify-content: space-between; padding: 20px 24px 18px; border-bottom: 1px solid var(--border); gap: 12px; }
  .st-modal-title { font-size: 15px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.3px; }
  .st-modal-sub   { font-size: 12px; color: var(--text-secondary); margin-top: 3px; }
  .st-modal-body  { padding: 22px 24px; display: flex; flex-direction: column; gap: 16px; overflow-y: auto; max-height: 70vh; }
  .st-modal-foot  { display: flex; align-items: center; justify-content: flex-end; gap: 8px; padding: 16px 24px; border-top: 1px solid var(--border); }
  .st-close-btn {
    width: 28px; height: 28px; border-radius: 7px; background: none; border: 1px solid var(--border);
    display: flex; align-items: center; justify-content: center; color: var(--text-muted); cursor: pointer; transition: all 0.15s; flex-shrink: 0;
  }
  .st-close-btn:hover { background: var(--rose-dim); color: var(--rose); border-color: rgba(251,113,133,0.25); }
  .st-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .st-detail-cell { padding: 12px 14px; border-radius: var(--radius-sm); background: rgba(255,255,255,0.025); border: 1px solid var(--border); }
  .st-detail-label { font-size: 9.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 6px; }
  .st-detail-val   { font-size: 13px; font-weight: 600; color: var(--text-primary); font-family: var(--font-mono); }
  .st-user-row { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-radius: var(--radius-sm); background: rgba(255,255,255,0.025); border: 1px solid var(--border); margin-bottom: 4px; }
  .st-section-label { font-size: 9.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; }
  .st-ghost-btn {
    display: flex; align-items: center; gap: 6px; padding: 7px 16px; border-radius: var(--radius-sm);
    background: none; border: 1px solid var(--border-hi); color: var(--text-secondary);
    font-size: 12px; font-weight: 500; cursor: pointer; transition: all 0.15s; font-family: var(--font-sans);
  }
  .st-ghost-btn:hover { background: var(--bg-hover); color: var(--text-primary); }

  /* Add order form */
  .st-form-row   { display: flex; flex-direction: column; gap: 5px; }
  .st-form-label { font-size: 10px; font-weight: 700; letter-spacing: 0.8px; text-transform: uppercase; color: var(--text-muted); }
  .st-form-input, .st-form-select {
    padding: 8px 12px; background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm); font-size: 12.5px; color: var(--text-primary);
    font-family: var(--font-sans); outline: none; transition: border-color 0.18s; width: 100%;
  }
  .st-form-input::placeholder { color: var(--text-muted); }
  .st-form-input:focus, .st-form-select:focus { border-color: rgba(99,102,241,0.45); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }
  .st-form-select option { background: var(--bg-elevated); }
  .st-save-btn {
    display: flex; align-items: center; gap: 6px; padding: 7px 18px; border-radius: var(--radius-sm);
    background: linear-gradient(135deg, var(--accent) 0%, #8b5cf6 100%);
    border: none; color: #fff; font-size: 12px; font-weight: 600; cursor: pointer;
    font-family: var(--font-sans); transition: opacity 0.18s; box-shadow: 0 4px 14px rgba(99,102,241,0.35);
  }
  .st-save-btn:hover:not(:disabled) { opacity: 0.88; }
  .st-save-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Mobile overlay */
  .st-overlay-mob { display: none; position: fixed; inset: 0; z-index: 40; background: rgba(0,0,0,.6); backdrop-filter: blur(2px); opacity: 0; transition: opacity .28s ease; pointer-events: none; }
  .st-overlay-mob.open { display: block; opacity: 1; pointer-events: auto; }

  /* Sidebar close (mobile) */
  .ad-logo-close { display: none; margin-left: auto; width: 28px; height: 28px; border-radius: 7px; background: none; border: none; cursor: pointer; align-items: center; justify-content: center; color: var(--text-muted); transition: all .15s; flex-shrink: 0; }
  .ad-logo-close:hover { background: var(--rose-dim); color: var(--rose); }

  /* Hamburger */
  .st-hamburger { display: none; width: 36px; height: 36px; border-radius: var(--radius-sm); background: var(--bg-card); border: 1px solid var(--border); cursor: pointer; align-items: center; justify-content: center; color: var(--text-secondary); transition: all .15s; flex-shrink: 0; }
  .st-hamburger:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,.25); }

  /* Page */
  .st-page { flex: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; z-index: 1; min-width: 0; }

  /* ── Responsive ── */
  @media (min-width: 1400px) { :root { --sidebar-w: 252px; } .st-main { padding: 30px 36px; } .st-topbar { padding: 0 36px; } }
  @media (max-width: 1280px) { .st-stat-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 1023px) { :root { --sidebar-w: 220px; } .st-main { padding: 20px; gap: 16px; } .st-topbar { padding: 0 16px; } }

  @media (max-width: 767px) {
    :root { --topbar-h: 56px; }
    .st-hamburger { display: flex; }
    .ad-logo-close { display: flex; }
    .ad-sidebar {
      position: fixed; top: 0; left: 0; bottom: 0;
      height: 100%; height: 100dvh; overflow: hidden;
      transform: translateX(-100%); box-shadow: 4px 0 32px rgba(0,0,0,.6); z-index: 50;
    }
    .ad-sidebar.open { transform: translateX(0); }
    .st-topbar { padding: 0 14px; }
    .st-topbar-sub { display: none; }
    .st-topbar-badge { display: none; }
    .st-main { padding: 14px; gap: 14px; }
    .st-stat-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
    .st-table-head { flex-direction: column; align-items: stretch; gap: 10px; }
    .st-filter-wrap { flex-wrap: wrap; }
    .st-search-wrap { width: 100%; }
    .st-search { width: 100%; }
    .st-pagination { flex-direction: column; align-items: flex-start; gap: 8px; }
    .st-detail-grid { grid-template-columns: 1fr; }
    .st-overlay { padding: 0; align-items: flex-end; }
    .st-modal { max-width: 100%; border-radius: var(--radius-md); max-height: 92vh; display: flex; flex-direction: column; }
    .st-modal-body { max-height: none; flex: 1; }
  }

  @media (max-width: 479px) {
    .st-stat-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
    .st-stat { padding: 14px 12px; }
    .st-stat-value { font-size: 21px; }
    .ad-sidebar { width: 80%; max-width: 280px; }
  }

  /* Gate */
  .st-gate { height: 100vh; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 14px; background: var(--bg-base); }
  .st-gate-icon { width: 36px; height: 36px; border-radius: 10px; background: linear-gradient(135deg,#6366f1,#a78bfa); box-shadow: 0 0 24px rgba(99,102,241,0.4); display: flex; align-items: center; justify-content: center; }
`;

function StyleInjector() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = STYLES;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);
  return null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDateTime(str) {
  if (!str) return "—";
  return new Date(str).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function fmtCurrency(val, currency = "USD") {
  if (val == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 2 }).format(val);
}
function userInitials(u) {
  const f = (u?.first_name ?? "").trim();
  const l = (u?.last_name  ?? "").trim();
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  if (f)      return f.slice(0, 2).toUpperCase();
  return "?";
}
function userName(u) {
  const f = u?.first_name ?? "";
  const l = u?.last_name  ?? "";
  return `${f} ${l}`.trim() || u?.email || "Unknown";
}
const PALETTE = ["indigo", "green", "amber", "rose", "purple"];
function avatarColor(id) {
  if (!id) return PALETTE[0];
  const code = typeof id === "string" ? id.charCodeAt(0) : id;
  return PALETTE[code % PALETTE.length];
}

// ─── Sidebar link ─────────────────────────────────────────────────────────────
function SidebarLink({ to, label, icon: Icon, end, onClick }) {
  return (
    <NavLink to={to} end={end} onClick={onClick} className={({ isActive }) => `ad-nav-link${isActive ? " active" : ""}`}>
      {({ isActive }) => (
        <>
          <Icon size={14} />
          <span style={{ flex: 1 }}>{label}</span>
          {isActive && <ChevronRight size={10} className="ad-nav-chevron" />}
        </>
      )}
    </NavLink>
  );
}

function SortIcon({ field, sortField, sortDir }) {
  if (field !== sortField) return <ChevronUp size={9} style={{ opacity: 0.2, marginLeft: 4, display: "inline" }} />;
  return sortDir === 1
    ? <ChevronUp   size={9} style={{ color: "var(--accent-light)", marginLeft: 4, display: "inline" }} />
    : <ChevronDown size={9} style={{ color: "var(--accent-light)", marginLeft: 4, display: "inline" }} />;
}

// ─── Status chip ──────────────────────────────────────────────────────────────
function StatusChip({ status }) {
  const map = {
    open:      { cls: "st-chip-open",      Icon: Clock,        label: "Open"      },
    filled:    { cls: "st-chip-filled",    Icon: CheckCircle2, label: "Filled"    },
    cancelled: { cls: "st-chip-cancelled", Icon: XCircle,      label: "Cancelled" },
  };
  const { cls, Icon, label } = map[status] ?? { cls: "st-chip-cancelled", Icon: XCircle, label: status };
  return <span className={`st-chip ${cls}`}><Icon size={9} />{label}</span>;
}

// ─── Order detail modal ───────────────────────────────────────────────────────
function OrderModal({ order, onClose }) {
  const u      = order.users;
  const stock  = order.stocks;
  const totalV = (order.quantity ?? 0) * (order.filled_price ?? order.limit_price ?? 0);
  const aColor = avatarColor(u?.id ?? "");

  const fields = [
    ["Symbol",       order.symbol ?? "—"],
    ["Stock",        stock?.name  ?? "—"],
    ["Exchange",     stock?.exchange ?? "—"],
    ["Sector",       stock?.sector   ?? "—"],
    ["Side",         <span className={`st-chip ${order.side === "buy" ? "st-chip-buy" : "st-chip-sell"}`}>{order.side?.toUpperCase()}</span>],
    ["Order type",   <span className={`st-chip ${order.order_type === "market" ? "st-chip-market" : "st-chip-limit"}`}>{order.order_type}</span>],
    ["Quantity",     order.quantity ?? "—"],
    ["Limit price",  fmtCurrency(order.limit_price)],
    ["Filled price", fmtCurrency(order.filled_price)],
    ["Total value",  totalV ? fmtCurrency(totalV) : "—"],
    ["Status",       <StatusChip status={order.status} />],
    ["Created",      fmtDateTime(order.created_at)],
  ];

  return (
    <div className="st-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="st-modal">
        <div className="st-modal-head">
          <div>
            <div className="st-modal-title">{order.symbol} order detail</div>
            <div className="st-modal-sub">Order ID: {order.id?.slice(0, 8)}…</div>
          </div>
          <button className="st-close-btn" onClick={onClose}><X size={13} /></button>
        </div>

        <div className="st-modal-body">
          {/* User row */}
          <div className="st-user-row">
            <div className={`st-avatar st-avatar-${aColor}`}>{userInitials(u)}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>{userName(u)}</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{u?.email}</div>
            </div>
            <StatusChip status={order.status} />
          </div>

          {/* Detail grid */}
          <div>
            <div className="st-section-label">Order details</div>
            <div className="st-detail-grid">
              {fields.map(([label, val]) => (
                <div key={label} className="st-detail-cell">
                  <div className="st-detail-label">{label}</div>
                  <div className="st-detail-val">{val}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="st-modal-foot">
          <button className="st-ghost-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Order Modal ──────────────────────────────────────────────────────────
function AddOrderModal({ onClose, onSaved }) {
  const [users,   setUsers]   = useState([]);
  const [stocks,  setStocks]  = useState([]);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState("");
  const [form, setForm] = useState({
    user_id:     "",
    symbol:      "",
    side:        "buy",
    order_type:  "market",
    quantity:    "",
    limit_price: "",
    filled_price:"",
    status:      "open",
  });

  useEffect(() => {
    supabase.from("users").select("id, first_name, last_name, email").order("first_name")
      .then(({ data }) => setUsers(data ?? []));
    supabase.from("stocks").select("symbol, name, exchange").eq("is_active", true).order("symbol")
      .then(({ data }) => setStocks(data ?? []));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function handleSave() {
    setErr("");
    if (!form.user_id)  return setErr("Please select a user.");
    if (!form.symbol)   return setErr("Please select a stock symbol.");
    if (!form.quantity) return setErr("Quantity is required.");
    if (form.order_type === "limit" && !form.limit_price) return setErr("Limit price is required for limit orders.");
    setSaving(true);
    const payload = {
      user_id:      form.user_id,
      symbol:       form.symbol,
      side:         form.side,
      order_type:   form.order_type,
      quantity:     parseFloat(form.quantity),
      limit_price:  form.limit_price  ? parseFloat(form.limit_price)  : null,
      filled_price: form.filled_price ? parseFloat(form.filled_price) : null,
      status:       form.status,
    };
    const { error } = await supabase.from("stock_orders").insert(payload);
    setSaving(false);
    if (error) return setErr(error.message);
    onSaved();
    onClose();
  }

  return (
    <div className="st-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="st-modal" style={{ maxWidth: 520 }}>
        <div className="st-modal-head">
          <div>
            <div className="st-modal-title">Add stock order</div>
            <div className="st-modal-sub">Manually place an order for a user</div>
          </div>
          <button className="st-close-btn" onClick={onClose}><X size={13} /></button>
        </div>

        <div className="st-modal-body" style={{ gap: 12 }}>
          {err && (
            <div style={{ padding: "10px 14px", borderRadius: 8, background: "var(--rose-dim)", border: "1px solid rgba(251,113,133,0.25)", fontSize: 12, color: "var(--rose)" }}>
              {err}
            </div>
          )}

          {/* User */}
          <div className="st-form-row">
            <label className="st-form-label">User *</label>
            <select className="st-form-select" value={form.user_id} onChange={(e) => set("user_id", e.target.value)}>
              <option value="">— select user —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>
              ))}
            </select>
          </div>

          {/* Symbol */}
          <div className="st-form-row">
            <label className="st-form-label">Stock symbol *</label>
            <select className="st-form-select" value={form.symbol} onChange={(e) => set("symbol", e.target.value)}>
              <option value="">— select stock —</option>
              {stocks.map((s) => (
                <option key={s.symbol} value={s.symbol}>{s.symbol} — {s.name} ({s.exchange})</option>
              ))}
            </select>
          </div>

          {/* Side + Order type */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="st-form-row">
              <label className="st-form-label">Side *</label>
              <select className="st-form-select" value={form.side} onChange={(e) => set("side", e.target.value)}>
                <option value="buy">BUY</option>
                <option value="sell">SELL</option>
              </select>
            </div>
            <div className="st-form-row">
              <label className="st-form-label">Order type *</label>
              <select className="st-form-select" value={form.order_type} onChange={(e) => set("order_type", e.target.value)}>
                <option value="market">Market</option>
                <option value="limit">Limit</option>
              </select>
            </div>
          </div>

          {/* Quantity */}
          <div className="st-form-row">
            <label className="st-form-label">Quantity *</label>
            <input className="st-form-input" type="number" step="any" min="0" placeholder="e.g. 10" value={form.quantity}
              onChange={(e) => set("quantity", e.target.value)} />
          </div>

          {/* Limit price + Filled price */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="st-form-row">
              <label className="st-form-label">Limit price {form.order_type === "limit" ? "*" : ""}</label>
              <input className="st-form-input" type="number" step="any" min="0" placeholder="optional"
                value={form.limit_price} onChange={(e) => set("limit_price", e.target.value)} />
            </div>
            <div className="st-form-row">
              <label className="st-form-label">Filled price</label>
              <input className="st-form-input" type="number" step="any" min="0" placeholder="if filled"
                value={form.filled_price} onChange={(e) => set("filled_price", e.target.value)} />
            </div>
          </div>

          {/* Status */}
          <div className="st-form-row">
            <label className="st-form-label">Status</label>
            <select className="st-form-select" value={form.status} onChange={(e) => set("status", e.target.value)}>
              <option value="open">Open</option>
              <option value="filled">Filled</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <div className="st-modal-foot">
          <button className="st-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="st-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Place order"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function StockTrades() {
  const navigate = useNavigate();

  const [adminEmail,    setAdminEmail]    = useState("");
  const [adminInitials, setAdminInitials] = useState("A");
  const [sessionReady,  setSessionReady]  = useState(false);

  const [orders,       setOrders]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField,    setSortField]    = useState("created_at");
  const [sortDir,      setSortDir]      = useState(-1);
  const [page,         setPage]         = useState(1);
  const [viewOrder,    setViewOrder]    = useState(null);
  const [addOrder,     setAddOrder]     = useState(false);

  // ── Mobile sidebar ───────────────────────────────────────────────────────
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = () => setSidebarOpen(false);
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  // ── Session gate ────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) { navigate("/admin/login", { replace: true }); return; }
      const email = session.user?.email ?? "";
      setAdminEmail(email);
      const parts = email.split("@")[0].split(".");
      setAdminInitials((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? parts[0]?.[1] ?? ""));
      setSessionReady(true);
    });
  }, [navigate]);

  async function handleLogout() {
    await supabase.auth.signOut();
    navigate("/admin/login", { replace: true });
  }

  // ── Fetch ───────────────────────────────────────────────────────────────
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("stock_orders")
      .select(`
        id, symbol, side, order_type, quantity,
        limit_price, filled_price, status, created_at,
        users ( id, first_name, last_name, email ),
        stocks ( name, exchange, sector, color_hex, initials, currency )
      `)
      .order("created_at", { ascending: false });
    if (error) console.error("[StockTrades] fetch error:", error.message);
    setOrders(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { if (sessionReady) fetchOrders(); }, [sessionReady, fetchOrders]);

  // ── Stats ───────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const filled    = orders.filter((o) => o.status === "filled");
    const totalVol  = filled.reduce((s, o) => s + (o.quantity ?? 0) * (o.filled_price ?? 0), 0);
    return {
      total:     orders.length,
      open:      orders.filter((o) => o.status === "open").length,
      filled:    filled.length,
      cancelled: orders.filter((o) => o.status === "cancelled").length,
      volume:    totalVol,
      buyCount:  orders.filter((o) => o.side === "buy").length,
      sellCount: orders.filter((o) => o.side === "sell").length,
    };
  }, [orders]);

  // ── Filter + sort ───────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let list = orders.filter((o) => {
      const name = userName(o.users).toLowerCase();
      const matchQ = !q || name.includes(q) || o.symbol?.toLowerCase().includes(q) || o.users?.email?.toLowerCase().includes(q);
      const matchS = statusFilter === "all" || o.status === statusFilter;
      return matchQ && matchS;
    });
    list.sort((a, b) => {
      if (sortField === "user")   return userName(a.users).localeCompare(userName(b.users)) * sortDir;
      if (sortField === "symbol") return (a.symbol ?? "").localeCompare(b.symbol ?? "") * sortDir;
      if (sortField === "value") {
        const av = (a.quantity ?? 0) * (a.filled_price ?? a.limit_price ?? 0);
        const bv = (b.quantity ?? 0) * (b.filled_price ?? b.limit_price ?? 0);
        return (av - bv) * sortDir;
      }
      return (new Date(a.created_at ?? 0) - new Date(b.created_at ?? 0)) * sortDir;
    });
    return list;
  }, [orders, search, statusFilter, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const pageSlice  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  function handleSort(field) {
    if (field === sortField) setSortDir((d) => d * -1);
    else { setSortField(field); setSortDir(-1); }
    setPage(1);
  }

  const STAT_CARDS = [
    { label: "Total orders",  value: stats.total,              icon: BarChart2,   bg: "rgba(99,102,241,0.15)",  color: "var(--accent-light)", glow: "rgba(99,102,241,0.08)",  bar: "var(--accent)",  sub: "All time"                          },
    { label: "Open orders",   value: stats.open,               icon: Clock,       bg: "rgba(251,191,36,0.15)",  color: "var(--amber)",        glow: "rgba(251,191,36,0.08)",  bar: "var(--amber)",   sub: "Pending fill"                      },
    { label: "Filled orders", value: stats.filled,             icon: TrendingUp,  bg: "rgba(52,211,153,0.15)",  color: "var(--green)",        glow: "rgba(52,211,153,0.08)",  bar: "var(--green)",   sub: `${stats.buyCount} buy · ${stats.sellCount} sell` },
    { label: "Total volume",  value: fmtCurrency(stats.volume),icon: Activity,    bg: "rgba(34,211,238,0.15)",  color: "var(--cyan)",         glow: "rgba(34,211,238,0.08)",  bar: "var(--cyan)",    sub: "Filled orders only"                },
  ];

  const SORT_COLS = [
    { key: "user",       label: "User"    },
    { key: "symbol",     label: "Symbol"  },
    { key: "created_at", label: "Date"    },
    { key: "value",      label: "Value"   },
  ];
  const STATIC_COLS = ["Side", "Type", "Qty", "Limit px", "Fill px", "Status", ""];

  if (!sessionReady) {
    return (
      <div className="st-gate">
        <StyleInjector />
        <div className="st-gate-icon"><BarChart2 size={16} color="#fff" /></div>
        <p style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "Inter, sans-serif" }}>Verifying session…</p>
      </div>
    );
  }

  return (
    <div className="st-shell">
      <StyleInjector />

      {/* Mobile overlay */}
      <div className={`st-overlay-mob${sidebarOpen ? " open" : ""}`} onClick={closeSidebar} aria-hidden="true" />

      {/* ── Sidebar ── */}
      <aside className={`ad-sidebar${sidebarOpen ? " open" : ""}`}>
        <div className="ad-logo">
          <div className="ad-logo-icon"><CandlestickChart size={16} color="#fff" /></div>
          <div className="ad-logo-text">
            <strong>vantrexmarket</strong>
            <span>Admin panel</span>
          </div>
          <button className="ad-logo-close" onClick={closeSidebar}><X size={14} /></button>
        </div>

        <nav className="ad-nav">
          {NAV_SECTIONS.map((section) => (
            <div key={section.label}>
              <p className="ad-nav-section-label">{section.label}</p>
              <div className="ad-nav-items">
                {section.items.map((item) => <SidebarLink key={item.to} {...item} onClick={closeSidebar} />)}
              </div>
            </div>
          ))}
        </nav>

        <div className="ad-user-card">
          <div className="ad-user-inner">
            <div className="ad-user-avatar">{adminInitials.toUpperCase()}</div>
            <div className="ad-user-info">
              <strong>Super Admin</strong>
              <span>{adminEmail}</span>
            </div>
            <button className="ad-logout-btn" onClick={handleLogout} title="Logout">
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Page ── */}
      <div className="st-page">

        {/* Topbar */}
        <header className="st-topbar">
          <div className="st-topbar-left">
            <button className="st-hamburger" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
              <Menu size={16} />
            </button>
            <div className="st-topbar-badge"><BarChart2 size={9} />Stock trades</div>
            <div>
              <div className="st-topbar-title">Stock trade management</div>
              <div className="st-topbar-sub">View and manage users' stock orders</div>
            </div>
          </div>
          <div className="st-topbar-right">
            <button className="st-btn" onClick={fetchOrders}><RefreshCw size={12} />Refresh</button>
            <button className="st-btn st-btn-accent" onClick={() => setAddOrder(true)}><Plus size={12} />Add order</button>
          </div>
        </header>

        <main className="st-main">

          {/* Stat cards */}
          <div className="st-stat-grid">
            {STAT_CARDS.map(({ label, value, icon: Icon, bg, color, glow, bar, sub }) => (
              <div key={label} className="st-stat" style={{ "--stat-glow": glow, "--stat-bar": bar }}>
                <div className="st-stat-top">
                  <span className="st-stat-label">{label}</span>
                  <div className="st-stat-icon" style={{ background: bg }}>
                    <Icon size={14} style={{ color }} />
                  </div>
                </div>
                <div>
                  <div className="st-stat-value">{value}</div>
                  {sub && <div className="st-stat-sub">{sub}</div>}
                </div>
              </div>
            ))}
          </div>

          {/* Table card */}
          <div className="st-table-card">
            <div className="st-table-head">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="st-table-title">Stock orders</span>
                <span className="st-table-count">{filtered.length}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {/* Status filter */}
                <div className="st-filter-wrap">
                  {["all", "open", "filled", "cancelled"].map((s) => (
                    <button key={s} className={`st-filter-pill${statusFilter === s ? " active" : ""}`}
                      onClick={() => { setStatusFilter(s); setPage(1); }}>
                      {s}
                    </button>
                  ))}
                </div>
                {/* Search */}
                <div className="st-search-wrap">
                  <Search size={12} className="st-search-icon" />
                  <input type="text" className="st-search" placeholder="Search user or symbol…"
                    value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
                </div>
              </div>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table className="st-table">
                <thead>
                  <tr>
                    {SORT_COLS.map(({ key, label }) => (
                      <th key={key} className="st-th" onClick={() => handleSort(key)}>
                        {label}<SortIcon field={key} sortField={sortField} sortDir={sortDir} />
                      </th>
                    ))}
                    {STATIC_COLS.map((h) => (
                      <th key={h} className={`st-th no-sort${h === "" ? " right" : ""}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <tr key={i} className="st-tr">
                        {Array.from({ length: 11 }).map((_, j) => (
                          <td key={j} className="st-td">
                            <div className="st-skel" style={{ height: 12, width: `${40 + Math.random() * 40}%` }} />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : pageSlice.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="st-td">
                        <div className="st-empty">
                          <div className="st-empty-icon"><AlertCircle size={16} /></div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>No orders found</span>
                          {search && <span style={{ fontSize: 11 }}>Try clearing your search or status filter</span>}
                        </div>
                      </td>
                    </tr>
                  ) : pageSlice.map((o) => {
                    const totalVal = (o.quantity ?? 0) * (o.filled_price ?? o.limit_price ?? 0);
                    const aColor   = avatarColor(o.users?.id ?? "");
                    const stockBg  = o.stocks?.color_hex ?? "#6366f1";
                    return (
                      <tr key={o.id} className="st-tr">
                        {/* User */}
                        <td className="st-td">
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div className={`st-avatar st-avatar-${aColor}`}>{userInitials(o.users)}</div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", whiteSpace: "nowrap" }}>{userName(o.users)}</div>
                              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{o.users?.email}</div>
                            </div>
                          </div>
                        </td>
                        {/* Symbol */}
                        <td className="st-td">
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div className="st-stock-badge" style={{ background: `${stockBg}22`, color: stockBg }}>
                              {o.stocks?.initials ?? o.symbol?.slice(0, 2)}
                            </div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)", fontFamily: "var(--font-mono)" }}>{o.symbol}</div>
                              <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{o.stocks?.name}</div>
                            </div>
                          </div>
                        </td>
                        {/* Date */}
                        <td className="st-td">
                          <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{fmtDateTime(o.created_at)}</span>
                        </td>
                        {/* Value */}
                        <td className="st-td">
                          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)", fontFamily: "var(--font-mono)" }}>
                            {totalVal ? fmtCurrency(totalVal, o.stocks?.currency) : "—"}
                          </span>
                        </td>
                        {/* Side */}
                        <td className="st-td">
                          <span className={`st-chip ${o.side === "buy" ? "st-chip-buy" : "st-chip-sell"}`}>
                            {o.side === "buy" ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
                            {o.side?.toUpperCase()}
                          </span>
                        </td>
                        {/* Type */}
                        <td className="st-td">
                          <span className={`st-chip ${o.order_type === "market" ? "st-chip-market" : "st-chip-limit"}`}>
                            {o.order_type}
                          </span>
                        </td>
                        {/* Qty */}
                        <td className="st-td">
                          <span style={{ fontSize: 12, color: "var(--text-secondary)", fontFamily: "var(--font-mono)" }}>{o.quantity ?? "—"}</span>
                        </td>
                        {/* Limit px */}
                        <td className="st-td">
                          <span style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>{fmtCurrency(o.limit_price)}</span>
                        </td>
                        {/* Fill px */}
                        <td className="st-td">
                          <span style={{ fontSize: 12, color: "var(--text-secondary)", fontFamily: "var(--font-mono)" }}>{fmtCurrency(o.filled_price)}</span>
                        </td>
                        {/* Status */}
                        <td className="st-td">
                          <StatusChip status={o.status} />
                        </td>
                        {/* Detail */}
                        <td className="st-td" style={{ textAlign: "right" }}>
                          <button className="st-icon-btn" onClick={() => setViewOrder(o)}>
                            <Eye size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="st-pagination">
                <span className="st-page-info">
                  Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length}
                </span>
                <div className="st-page-btns">
                  {Array.from({ length: Math.min(totalPages, 8) }, (_, i) => i + 1).map((p) => (
                    <button key={p} className={`st-page-btn${page === p ? " active" : ""}`} onClick={() => setPage(p)}>{p}</button>
                  ))}
                </div>
              </div>
            )}
          </div>

        </main>
      </div>

      {viewOrder && <OrderModal order={viewOrder} onClose={() => setViewOrder(null)} />}
      {addOrder  && <AddOrderModal onClose={() => setAddOrder(false)} onSaved={fetchOrders} />}
    </div>
  );
}